import { useFilesByTeamId, useFile } from '@/queries/files';
import FileOutlined from '@ant-design/icons/FileOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Input from 'antd/es/input';
import Select from 'antd/es/select';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import type { GetFileByIdResponse } from '@mai/platform-api/src/routes/files.schema';
import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;

const SelectContainer = styled.div`
  ${({ theme }) => `
  position: relative;
  
  .ant-select {
    width: 100%;
  }
  
  .ant-select-selection-item {
    display: flex;
    align-items: center;
    gap: ${theme.marginXS}px;
  }
`}
`;

const FileInfo = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginSM}px;
  padding: ${theme.paddingSM}px ${theme.padding}px;
  background: ${theme.colorFillQuaternary};
  border-radius: ${theme.borderRadius}px;
  margin-top: ${theme.marginSM}px;
`}
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileMeta = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
`}
`;

const EmptyState = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingLG}px;
  text-align: center;
  color: ${theme.colorTextSecondary};
  font-size: ${theme.fontSize}px;
  border: ${theme.lineWidth}px dashed ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  background: ${theme.colorBgContainer};
`}
`;

const SearchInputWrapper = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingXS}px;
`}
`;

const FileOptionContainer = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
`}
`;

const FileOptionIcon = styled(FileOutlined)`
  ${({ theme }) => `
  color: ${theme.colorPrimary};
`}
`;

const FileOptionDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileOptionName = styled.div`
  ${({ theme }) => `
  font-weight: ${theme.fontWeightStrong};
`}
`;

const FileOptionMeta = styled.div`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;
`}
`;

const NotFoundContent = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingSM}px;
  text-align: center;
`}
`;

const HelperText = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  margin-top: ${theme.marginXS}px;
  display: block;
`}
`;

const EmptyStateIcon = styled(FileOutlined)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeLG}px;
  margin-bottom: ${theme.marginSM}px;
`}
`;

const EmptyStateText = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
`}
`;

interface FileSelectFieldProps {
  value?: string; // file_id
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  readonly?: boolean;
  teamId: string;
  schema?: JSONSchemaType & {
    description?: string;
    accept?: string;
  };
  placeholder?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileSelectFieldInner: React.FC<FileSelectFieldProps> = ({
  value,
  onChange,
  disabled = false,
  readonly = false,
  teamId,
  schema,
  placeholder = 'Select a file',
}) => {
  const { message } = App.useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();

  // Fetch files for the team
  const { data: filesResponse, isLoading: isLoadingFiles } = useFilesByTeamId(teamId);
  
  // Fetch details of the selected file
  const { data: selectedFile } = useFile(value || '', { enabled: !!value });

  const files = filesResponse?.items || [];
  
  // Filter files based on search term and accept types
  const filteredFiles = files.filter((file: GetFileByIdResponse) => {
    // Check search term
    const matchesSearch = file.fileName.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    
    // Check accept types if specified
    if (schema?.accept && schema.accept !== '*/*') {
      const acceptedExtensions = schema.accept
        .split(',')
        .map((t: string) => t.trim());
      const fileExtension = `.${file.fileName.split('.').pop()?.toLowerCase()}`;
      
      const fileMatchesType = acceptedExtensions.some((accepted: string) => {
        if (accepted.startsWith('.')) {
          return fileExtension === accepted.toLowerCase();
        }
        // For MIME types, we'd need to infer from extension or store mimeType in the file response
        // For now, we'll skip MIME type matching for the select field
        return false;
      });
      
      if (!fileMatchesType) return false;
    }
    
    return true;
  });

  const handleFileSelect = (fileId: string) => {
    onChange?.(fileId);
    void message.success('File selected successfully!');
  };

  const handleRemove = () => {
    onChange?.(undefined);
    void message.success('File selection cleared!');
  };

  // Show selected file info if we have a selection
  if (value && selectedFile) {
    return (
      <>
        <FileInfo>
          <FileOutlined style={{ fontSize: theme.fontSizeLG, color: theme.colorPrimary }} />
          <FileDetails>
            <FileName strong>{selectedFile.fileName}</FileName>
            <FileMeta>
              {formatFileSize(selectedFile.fileSize)}
              {selectedFile.createdAt && (
                <> • {new Date(selectedFile.createdAt).toLocaleDateString()}</>
              )}
            </FileMeta>
          </FileDetails>
          {!disabled && !readonly && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              size="small"
            >
              Remove
            </Button>
          )}
        </FileInfo>
      </>
    );
  }

  // Show loading state
  if (isLoadingFiles) {
    return (
      <EmptyState>
        <LoadingOutlined /> Loading files...
      </EmptyState>
    );
  }

  // Show empty state if no files
  if (files.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIcon />
        <div>No files available</div>
        <EmptyStateText type="secondary">
          Upload a file first to use it in pipelines
        </EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <SelectContainer>
      <Select
        placeholder={placeholder}
        value={value}
        onChange={handleFileSelect}
        disabled={disabled || readonly}
        showSearch
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        filterOption={false}
        optionFilterProp="children"
        dropdownRender={(menu) => (
          <div>
            <SearchInputWrapper>
              <Input
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                size="small"
              />
            </SearchInputWrapper>
            {menu}
          </div>
        )}
        options={filteredFiles.map((file: GetFileByIdResponse) => ({
          value: file.fileId,
          label: (
            <FileOptionContainer>
              <FileOptionIcon />
              <FileOptionDetails>
                <FileOptionName>
                  {file.fileName}
                </FileOptionName>
                <FileOptionMeta>
                  <span>{formatFileSize(file.fileSize)}</span>
                  <span>•</span>
                  <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                </FileOptionMeta>
              </FileOptionDetails>
            </FileOptionContainer>
          ),
        }))}
        notFoundContent={
          searchTerm ? (
            <NotFoundContent>
              <Text type="secondary">No files found matching "{searchTerm}"</Text>
            </NotFoundContent>
          ) : (
            <NotFoundContent>
              <Text type="secondary">No files available</Text>
            </NotFoundContent>
          )
        }
      />
      {schema?.description && (
        <HelperText type="secondary">
          {schema.description}
        </HelperText>
      )}
    </SelectContainer>
  );
};

// Export wrapper that works with Ant Design Form.Item
export const FileSelectField = React.forwardRef<
  HTMLDivElement,
  FileSelectFieldProps & {
    value?: string;
    onChange?: (value: string | undefined) => void;
  }
>((props, ref) => {
  return (
    <div ref={ref}>
      <FileSelectFieldInner {...props} />
    </div>
  );
});

FileSelectField.displayName = 'FileSelectField';